import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Spinner, Alert } from 'react-bootstrap';
import SignalTable from '../components/SignalTable';
import { signalsApi } from '../services/api';

const HomePage = () => {
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Filtering state
  const [filters, setFilters] = useState({
    searchTerm: '',
    signalType: 'all',
    timeframe: 'all',
    sortField: 'timestamp',
    sortDirection: 'desc'
  });
  
  // Auto-hide success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage]);
  
  // Function to fetch signals
  const fetchSignals = useCallback(async (token = null) => {
    try {
      setLoading(true);
      
      const response = await signalsApi.getAll(50, token, filters);
      
      if (token) {
        // Append to existing signals for pagination
        setSignals(prevSignals => [...prevSignals, ...response.signals]);
      } else {
        // First load
        setSignals(response.signals || []);
      }
      
      // Update pagination state
      setNextToken(response.nextToken || null);
      setHasMore(!!response.nextToken);
    } catch (err) {
      console.error('Error fetching signals:', err);
      setError('Failed to load signals. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [filters]);
  
  // Fetch signals on component mount and when filters change
  useEffect(() => {
    setNextToken(null);
    fetchSignals();
  }, [fetchSignals]);
  
  // Load more signals
  const handleLoadMore = () => {
    if (nextToken) {
      fetchSignals(nextToken);
    }
  };
  
  // Handle refresh
  const handleRefresh = () => {
    setNextToken(null);
    fetchSignals();
  };
  
  // Handle filter changes from SignalTable
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };
  
  // Handle signals deletion
  const handleSignalsDeleted = (result) => {
    // Show success message
    if (result.results) {
      // For batch delete operations
      const { deleted, failed } = result.results;
      if (failed && failed.length > 0) {
        setSuccessMessage(`Successfully deleted ${deleted.length} signals. Failed to delete ${failed.length} signals.`);
      } else {
        setSuccessMessage(`Successfully deleted ${deleted.length} signals.`);
      }
    } else {
      // For single delete operations
      setSuccessMessage('Signal deleted successfully.');
    }
    
    // Refresh signals
    handleRefresh();
  };
  
  return (
    <div className="home-page">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h2 className="mb-0">Recent Signals</h2>
          <Button 
            variant="outline-primary" 
            onClick={handleRefresh} 
            disabled={loading}
          >
            {loading && !signals.length ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              <i className="bi bi-arrow-clockwise"></i>
            )}{' '}
            Refresh
          </Button>
        </Card.Header>
        <Card.Body>
          {/* Success message for deletion operations */}
          {successMessage && (
            <Alert 
              variant="success" 
              onClose={() => setSuccessMessage(null)} 
              dismissible
              className="mb-4"
            >
              <i className="bi bi-check-circle-fill me-2"></i>
              {successMessage}
            </Alert>
          )}
          
          {error ? (
            <div className="text-center text-danger my-4">
              <p>{error}</p>
              <Button variant="primary" onClick={handleRefresh}>
                Try Again
              </Button>
            </div>
          ) : (
            <>
              <SignalTable 
                signals={signals} 
                loading={loading && !signals.length} 
                onFilterChange={handleFilterChange}
                initialFilters={filters}
                onSignalsDeleted={handleSignalsDeleted}
              />
              
              {hasMore && (
                <div className="text-center mt-4">
                  <Button 
                    variant="outline-secondary" 
                    onClick={handleLoadMore}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default HomePage;